import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns/format';

import HighlightText from '@adsk/alloy-react-highlight-text';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { isNonEmptyString } from './commons';
import PackagesAvatar from '../components/PackagesAvatar';
import PackagesLockedIcon from '../components/PackagesLockedIcon';

const columnHelper = createColumnHelper();

const wrapOverflowTooltip = (columns) => {
  return columns.map((column) => {
    const newColumn = {};

    // Wrap plain text header with OverflowTooltip
    if (isNonEmptyString(column.header)) {
      newColumn.header = () => {
        return (
          <OverflowTooltip
            autoTipOnEllipsis
            triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
          >
            {column.header}
          </OverflowTooltip>
        );
      };
    }

    // Wrap plain text cell with OverflowTooltip
    if (!column.cell) {
      newColumn.cell = (props) => {
        return (
          <OverflowTooltip
            autoTipOnEllipsis
            triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
          >
            {props.getValue()}
          </OverflowTooltip>
        );
      };
    }

    if (column.cell) {
      newColumn.cell = (props) => {
        const calculatedCell = column.cell(props);

        if (!isNonEmptyString(calculatedCell)) {
          return calculatedCell;
        }
        return (
          <OverflowTooltip
            autoTipOnEllipsis
            triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
          >
            {calculatedCell}
          </OverflowTooltip>
        );
      };
    }

    return {
      ...column,
      ...newColumn,
    };
  });
};

const getPackageColumns = ({ T, searchExpression }) => {
  const renderWithHighlight = (props) => {
    if (!searchExpression || !searchExpression.trim()) {
      return (
        <OverflowTooltip
          autoTipOnEllipsis
          triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
        >
          {props.getValue()}
        </OverflowTooltip>
      );
    }

    return (
      <OverflowTooltip
        autoTipOnEllipsis
        triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
      >
        <HighlightText
          matched={searchExpression.trim()}
          text={String(props.getValue())}
        />
      </OverflowTooltip>
    );
  };

  return wrapOverflowTooltip([
    columnHelper.accessor('displayId', {
      id: 'displayId',
      size: 100,
      minSize: 50,
      header: T.translate('TABLE_COLUMN.ID'),
      cell: renderWithHighlight,
    }),
    columnHelper.accessor('name', {
      id: 'name',
      minSize: 150,
      header: T.translate('TABLE_COLUMN.NAME'),
      cell: (props) => {
        const rowData = props.row.original;

        return (
          <>
            <div style={{ overflow: 'hidden' }}>
              {renderWithHighlight(props)}
            </div>
            {props.row.original.locked && <PackagesLockedIcon {...rowData} />}
          </>
        );
      },
    }),
    columnHelper.accessor('description', {
      id: 'description',
      minSize: 150,
      enableSorting: false,
      header: T.translate('TABLE_COLUMN.DESCRIPTION'),
      cell: renderWithHighlight,
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      minSize: 100,
      header: T.translate('TABLE_COLUMN.CREATED_AT'),
      cell: (props) => {
        return format(props.getValue(), 'PP p');
      },
    }),
    columnHelper.accessor('createdBy', {
      id: 'createdBy',
      minSize: 100,
      enableSorting: false,
      header: T.translate('TABLE_COLUMN.CREATED_BY'),
      cell: (props) => {
        return <PackagesAvatar oxygenId={props.getValue()} />;
      },
    }),
    columnHelper.accessor('updatedAt', {
      id: 'updatedAt',
      minSize: 100,
      header: T.translate('TABLE_COLUMN.UPDATED_AT'),
      cell: (props) => {
        return format(props.getValue(), 'PP p');
      },
    }),
    columnHelper.accessor('updatedBy', {
      id: 'updatedBy',
      minSize: 100,
      enableSorting: false,
      header: T.translate('TABLE_COLUMN.UPDATED_BY'),
      cell: (props) => {
        return <PackagesAvatar oxygenId={props.getValue()} />;
      },
    }),
    columnHelper.accessor('resourceCount', {
      id: 'resourceCount',
      minSize: 100,
      enableSorting: false,
      header: () => {
        return (
          <>
            {T.translate('TABLE_COLUMN.TOTAL_FILES')}
            <Tooltip
              shouldWrapChildren={false}
              content={T.translate('TABLE_COLUMN.TOTAL_FILES_TOOLTIP')}
            >
              <AlertInformationIcon
                size={16}
                style={{ marginLeft: 4, color: theme.colors.adskBlue600 }}
              />
            </Tooltip>
          </>
        );
      },
    }),
  ]);
};

export default getPackageColumns;
